import { useTranslation as useI18nextTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { setCookie } from '../utils/cookie';

/**
 * 封装 react-i18next 的 useTranslation hook
 * 提供类型安全的翻译函数
 * 
 * @author dongdong.fdd
 * @date 2026/1/25
 */
export const useTranslation = () => {
  const { t, i18n } = useI18nextTranslation();
  const [currentLanguage, setCurrentLanguage] = useState<'zh' | 'en'>(() => {
    const lang = i18n.language;
    if (lang.startsWith('zh')) {
      return 'zh';
    }
    return 'en';
  });

  /**
   * 翻译函数，支持插值
   * @param key 翻译键
   * @param options 插值选项
   */
  const translate = (key: string, options?: Record<string, any>) => {
    return t(key, options);
  };

  /**
   * 切换语言
   * @param lng 语言代码 (zh | en)
   */
  const changeLanguage = async (lng: 'zh' | 'en') => {
    await i18n.changeLanguage(lng);
    // 确保 cookie 也被更新（i18next-browser-languagedetector 应该会自动处理，但为了确保，我们也手动设置）
    setCookie('i18nextLng', lng, 365, '/');
  };

  // 监听语言变化
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      if (lng.startsWith('zh')) {
        setCurrentLanguage('zh');
      } else {
        setCurrentLanguage('en');
      }
    };

    // 初始化时设置一次
    handleLanguageChange(i18n.language);

    // 监听语言变化事件
    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  return {
    t: translate,
    i18n,
    currentLanguage,
    changeLanguage,
  };
};
